import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useOrg } from '@/contexts/OrgContext';
import { cn } from '@/lib/utils';
import { useServicesSectionData } from '@/hooks/useServicesSectionData';

interface ServicesSectionProps {
  content: {
    title?: string;
    subtitle?: string;
    showPrices?: boolean;
    categories?: string[];
  };
  variant?: string;
  layoutStyle?: string;
}

export function ServicesSection({ content, variant = 'default', layoutStyle = 'classic' }: ServicesSectionProps) {
  const {
    title = 'Our Services',
    subtitle,
    showPrices = true,
    categories,
  } = content;

  const { orgId } = useOrg();

  const isMinimal = layoutStyle === 'modern-minimal';
  const isBold = layoutStyle === 'bold-geometric';

  const { data: services = [], isLoading: loading } = useServicesSectionData(orgId, categories);

  // Group by category
  const groupedServices = services.reduce((acc, service) => {
    const cat = service.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(service);
    return acc;
  }, {} as Record<string, typeof services>);

  if (loading) {
    return (
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">Loading services...</div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn(
      "section-padding",
      variant === 'alt-bg' ? "bg-secondary/30" : "bg-background"
    )}>
      <div className="container mx-auto px-4">
        {(title || subtitle) && (
          <div className="text-center max-w-2xl mx-auto mb-12">
            {title && (
              <h2 className={cn(
                "font-display font-bold text-foreground mb-4",
                isBold ? "text-3xl uppercase tracking-wide" : "text-3xl md:text-4xl"
              )}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-muted-foreground text-lg">{subtitle}</p>
            )}
          </div>
        )}

        <div className="space-y-12">
          {Object.entries(groupedServices).map(([category, categoryServices]) => (
            <div key={category}>
              <h3 className={cn(
                "font-display font-semibold text-foreground mb-6",
                isBold ? "text-xl uppercase tracking-wide" : "text-2xl"
              )}>
                {category}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryServices.map((service) => (
                  <Link
                    key={service.id}
                    to="/booking"
                    className={cn(
                      "group p-6 transition-all",
                      isMinimal ? "border-b border-border hover:border-foreground" :
                      isBold ? "border-2 border-foreground hover:bg-foreground hover:text-background" :
                      "bg-card rounded-2xl border hover:shadow-lg"
                    )}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-foreground group-hover:text-primary">
                        {service.name}
                      </h4>
                      {showPrices && (
                        <span className="text-primary font-semibold">
                          {service.price}
                        </span>
                      )}
                    </div>
                    {service.description && (
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {service.description}
                      </p>
                    )}
                    <div className="flex items-center gap-1 mt-4 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Book now
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
